import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useOrgMemberships } from '@/contexts/org-memberships-context';
import {
  getOnboardingState,
  updateOnboardingStep,
  completeOnboarding,
  getNextOnboardingStep,
  migrateOldInstallIntent,
  type OnboardingState,
} from '@/lib/onboarding';

/**
 * Hook to manage onboarding flow
 */
export function useOnboarding() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { userMemberships } = useOrgMemberships();

  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);

  // Load onboarding state on mount
  useEffect(() => {
    // Migrate old install_intent if exists
    migrateOldInstallIntent();

    // Load current state
    const state = getOnboardingState();
    setOnboardingState(state);
  }, []);

  /**
   * Check if user needs onboarding
   */
  const needsOnboarding = (): boolean => {
    return onboardingState !== null && onboardingState.intent !== null;
  };

  /**
   * Get next required step
   */
  const getNextStep = (storeId?: string): string | null => {
    if (!onboardingState) return null;

    const hasOrganization = !!organization || (userMemberships?.data?.length ?? 0) > 0;
    const hasStore = !!storeId;

    return getNextOnboardingStep({
      hasOrganization,
      hasStore,
      state: onboardingState,
    });
  };

  /**
   * Complete onboarding and return redirect URL
   * Caller is responsible for navigation
   */
  const complete = (orgSlug: string, storeId?: string): string => {
    const redirectUrl = completeOnboarding({ orgSlug, storeId });
    return redirectUrl;
  };

  /**
   * Update current step
   */
  const updateStep = (step: string): void => {
    updateOnboardingStep(step);
    // Refresh state
    setOnboardingState(getOnboardingState());
  };

  return {
    state: onboardingState,
    needsOnboarding: needsOnboarding(),
    getNextStep,
    complete,
    updateStep,
    intentType: onboardingState?.intent?.type,
    pluginName: onboardingState?.intent?.pluginName,
  };
}
